import React, { useState, useEffect } from 'react';
import { 
  Banknote, Car, Clock, CheckCircle, AlertTriangle, Lock,
  User, Calendar, RefreshCw, Search, ChevronDown, X,
  DollarSign, TrendingUp, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatTime, formatDate, formatNumber } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CashDeskPage = () => {
  const [shifts, setShifts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // open, closed, problem
  const [selectedShift, setSelectedShift] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch shifts
      let shiftsUrl = `${API_URL}/api/shifts`;
      if (filter === 'open') {
        shiftsUrl += '?status=active';
      } else if (filter === 'closed') {
        shiftsUrl += '?status=completed';
      } else if (filter === 'problem') {
        shiftsUrl += '?has_difference=true';
      }

      const [shiftsRes, driversRes] = await Promise.all([
        fetch(shiftsUrl, { headers }),
        fetch(`${API_URL}/api/drivers`, { headers })
      ]);

      if (shiftsRes.ok) {
        const shiftsData = await shiftsRes.json();
        setShifts(shiftsData);
        
        // Calculate stats
        const openShifts = shiftsData.filter(s => s.status === 'active');
        const closedShifts = shiftsData.filter(s => s.status === 'completed');
        const problemShifts = shiftsData.filter(s => s.cash_difference && s.cash_difference !== 0);
        
        setStats({
          open: openShifts.length,
          closed: closedShifts.length,
          problem: problemShifts.length,
          totalCash: closedShifts.reduce((sum, s) => sum + (s.cash_collected || 0), 0)
        });
      }

      if (driversRes.ok) {
        const driversData = await driversRes.json();
        setDrivers(driversData);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseShift = async () => {
    if (!cashReceived) {
      toast.error('Fut shumën e cash të marrë');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/driver-shifts/${selectedShift.id}/end`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cash_collected: parseFloat(cashReceived),
          notes: closingNotes
        })
      });

      if (response.ok) {
        toast.success('Turni u mbyll me sukses!');
        setShowCloseModal(false);
        setCashReceived('');
        setClosingNotes('');
        setSelectedShift(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Gabim');
      }
    } catch (error) {
      toast.error('Gabim në lidhje me serverin');
    }
  };

  // Formatting functions moved to utils/formatters.js

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cash Desk</h1>
          <p className="text-gray-500">Mbyllja e turneve dhe menaxhimi i cash</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div 
          onClick={() => setFilter('open')}
          className={`bg-white rounded-xl p-5 border cursor-pointer transition ${
            filter === 'open' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-100 hover:border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Turne të Hapura</p>
              <p className="text-2xl font-bold text-green-600">{stats?.open || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFilter('closed')}
          className={`bg-white rounded-xl p-5 border cursor-pointer transition ${
            filter === 'closed' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-100 hover:border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Turne të Mbyllura</p>
              <p className="text-2xl font-bold text-blue-600">{stats?.closed || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFilter('problem')}
          className={`bg-white rounded-xl p-5 border cursor-pointer transition ${
            filter === 'problem' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-100 hover:border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Me Diferencë</p>
              <p className="text-2xl font-bold text-red-600">{stats?.problem || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Cash i Mbledhur Sot</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.totalCash)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Banknote className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Shifts Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shoferi</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filloi</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Udhëtime</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Fare</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Komision</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cash Marrë</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Diferencë</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <RefreshCw className="w-6 h-6 mx-auto mb-2 text-gray-300 animate-spin" />
                    <p className="text-sm text-gray-500">Duke ngarkuar...</p>
                  </td>
                </tr>
              ) : shifts.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <Clock className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                    <p className="text-sm text-gray-500">Nuk ka turne</p>
                  </td>
                </tr>
              ) : (
                shifts.map(shift => {
                  const hasDifference = shift.cash_difference && shift.cash_difference !== 0;
                  const isLocked = shift.locked;
                  
                  return (
                    <tr key={shift.id} className={`hover:bg-gray-50 ${hasDifference ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                            <Car className="w-5 h-5 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{shift.driver_name}</p>
                            <p className="text-xs text-gray-500">{shift.vehicle_plate || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm">{formatTime(shift.start_time)}</p>
                          <p className="text-xs text-gray-500">{formatDate(shift.start_time)}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-gray-100 rounded-lg text-sm font-medium">
                          {shift.total_rides || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(shift.total_fare)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {formatCurrency(shift.company_commission)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-green-600">
                        {shift.cash_collected ? formatCurrency(shift.cash_collected) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {shift.cash_difference !== undefined && shift.cash_difference !== null ? (
                          <span className={`font-bold ${
                            shift.cash_difference === 0 
                              ? 'text-gray-500' 
                              : shift.cash_difference > 0 
                                ? 'text-green-600' 
                                : 'text-red-600'
                          }`}>
                            {shift.cash_difference > 0 ? '+' : ''}{formatCurrency(shift.cash_difference)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          shift.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : isLocked
                              ? 'bg-gray-100 text-gray-600'
                              : hasDifference
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-700'
                        }`}>
                          {isLocked && <Lock className="w-3 h-3" />}
                          {shift.status === 'active' ? 'Aktiv' : isLocked ? 'Kyçur' : 'Mbyllur'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {shift.status === 'active' && (
                          <button
                            onClick={() => {
                              setSelectedShift(shift);
                              setShowCloseModal(true);
                            }}
                            className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700"
                          >
                            Mbyll Turnin
                          </button>
                        )}
                        {shift.status === 'completed' && !isLocked && (
                          <button
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              await fetch(`${API_URL}/api/shifts/${shift.id}/lock`, {
                                method: 'PUT',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              toast.success('Turni u kyç');
                              fetchData();
                            }}
                            className="px-3 py-1.5 bg-gray-600 text-white text-xs font-medium rounded-lg hover:bg-gray-700"
                          >
                            Kyç
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Close Shift Modal */}
      {showCloseModal && selectedShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold">Mbyll Turnin</h3>
              <button 
                onClick={() => {
                  setShowCloseModal(false);
                  setCashReceived('');
                  setClosingNotes('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Driver Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Car className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-bold">{selectedShift.driver_name}</p>
                    <p className="text-sm text-gray-500">Turni filloi: {formatTime(selectedShift.start_time)}</p>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Udhëtime</span>
                  <span className="font-medium">{selectedShift.total_rides || 0}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Total Fare</span>
                  <span className="font-medium">{formatCurrency(selectedShift.total_fare)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Komision Kompanie</span>
                  <span className="font-medium text-red-600">-{formatCurrency(selectedShift.company_commission)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Për Dorëzim</span>
                  <span className="font-bold text-lg">
                    {formatCurrency((selectedShift.total_fare || 0) - (selectedShift.company_commission || 0))}
                  </span>
                </div>
              </div>

              {/* Cash Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cash i marrë realisht
                </label>
                <div className="relative">
                  <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-500" />
                  <input
                    type="number"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    placeholder="0"
                    className="w-full pl-11 pr-16 py-3 border border-gray-200 rounded-xl text-lg font-bold focus:ring-2 focus:ring-red-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">Lekë</span>
                </div>
              </div>

              {/* Difference Preview */}
              {cashReceived && (
                <div className={`p-3 rounded-xl ${
                  parseFloat(cashReceived) === ((selectedShift.total_fare || 0) - (selectedShift.company_commission || 0))
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Diferencë:</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(
                        parseFloat(cashReceived) - ((selectedShift.total_fare || 0) - (selectedShift.company_commission || 0))
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shënime (opsionale)
                </label>
                <textarea
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  placeholder="Shënime për mbylljen e turnit..."
                  rows={2}
                  className="w-full p-3 border border-gray-200 rounded-xl resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => {
                  setShowCloseModal(false);
                  setCashReceived('');
                  setClosingNotes('');
                }}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium hover:bg-gray-50"
              >
                Anulo
              </button>
              <button
                onClick={handleCloseShift}
                disabled={!cashReceived}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50"
              >
                Mbyll Turnin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashDeskPage;
